(() => {
  if (customElements.get('sleepcity-mattress-selector')) return;

  class SleepCityMattressSelector extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.state = {
        sleepers: '',
        size: '',
        position: '',
        position_1: '',
        position_2: '',
        support: '',
        feel: '',
        feel_1: '',
        feel_2: '',
        temperature: '',
        motion: '',
        budget: '',
      };

      this.quiz = this.querySelector('[data-quiz]');
      this.results = this.querySelector('[data-results]');
      this.progressbar = this.querySelector('[data-progressbar]');
      this.progress = this.querySelector('[data-progress]');
      this.stepLabel = this.querySelector('[data-step-label]');
      this.currentStep = 1;

      this.bindOptions();
      this.bindNavigation();
      this.bindCalculator();
      this.updateSleeperLayout();
      this.setStep(1, false);
    }

    bindOptions() {
      this.querySelectorAll('[data-field][data-value]').forEach((button) => {
        button.addEventListener('click', () => this.selectOption(button));
      });
    }

    bindNavigation() {
      this.querySelectorAll('[data-next]').forEach((button) => {
        button.addEventListener('click', () => {
          const steps = this.getActiveSteps();
          const index = steps.indexOf(this.currentStep);
          if (index < steps.length - 1) this.setStep(steps[index + 1]);
        });
      });

      this.querySelectorAll('[data-back]').forEach((button) => {
        button.addEventListener('click', () => {
          const steps = this.getActiveSteps();
          const index = steps.indexOf(this.currentStep);
          if (index > 0) this.setStep(steps[index - 1]);
        });
      });

      this.querySelector('[data-finish]')?.addEventListener('click', () => this.renderResults());
      this.querySelector('[data-restart]')?.addEventListener('click', () => this.restart());
    }

    bindCalculator() {
      const calculator = this.querySelector('[data-calculator]');
      const costInput = calculator?.querySelector('[data-calculator-cost]');
      const monthsInput = calculator?.querySelector('[data-calculator-months]');
      const costOutput = calculator?.querySelector('[data-calculator-cost-output]');
      const monthsOutput = calculator?.querySelector('[data-calculator-months-output]');
      const output = calculator?.querySelector('[data-calculator-output]');
      if (!calculator || !costInput || !monthsInput || !costOutput || !monthsOutput || !output) return;

      const currency = window.Shopify?.currency?.active || 'USD';
      const locale = document.documentElement.lang || 'en';
      const formatter = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

      const update = () => {
        const cost = Number(costInput.value);
        const months = Number(monthsInput.value);
        const monthlyPayment = cost > 0 && months > 0 ? cost / months : 0;
        costOutput.textContent = formatter.format(cost);
        monthsOutput.textContent = `${months} months`;
        output.textContent = formatter.format(monthlyPayment);
      };

      costInput.addEventListener('input', update);
      monthsInput.addEventListener('input', update);
      update();
    }

    selectOption(button) {
      const field = button.dataset.field;
      const value = button.dataset.value;

      this.querySelectorAll(`[data-field="${field}"]`).forEach((option) => {
        option.setAttribute('aria-pressed', String(option === button));
      });
      this.state[field] = value;

      if (field === 'sleepers') {
        this.resetSleeperDependentAnswers(value);
        this.updateSleeperLayout();
        this.updateSizeGuidance();
        this.updatePreview('[data-sleeper-preview]', button.dataset.preview);
      }

      if (field === 'position') {
        this.updatePreview('[data-position-preview]', button.dataset.preview);
      }

      this.updateStepValidity(this.currentStep);
    }

    resetSleeperDependentAnswers(sleeperType) {
      if (sleeperType === 'couple') {
        this.state.position = '';
        this.state.feel = '';
        this.clearFieldSelection('position');
        this.clearFieldSelection('feel');
      } else {
        this.state.position_1 = '';
        this.state.position_2 = '';
        this.state.feel_1 = '';
        this.state.feel_2 = '';
        this.state.motion = '';
        ['position_1', 'position_2', 'feel_1', 'feel_2', 'motion'].forEach((field) => {
          this.clearFieldSelection(field);
        });
      }
    }

    clearFieldSelection(field) {
      this.querySelectorAll(`[data-field="${field}"]`).forEach((button) => {
        button.setAttribute('aria-pressed', 'false');
      });
    }

    updateSleeperLayout() {
      const isCouple = this.state.sleepers === 'couple';
      const hasSleeper = Boolean(this.state.sleepers);

      this.querySelector('[data-size-group]')?.toggleAttribute('hidden', !hasSleeper);
      this.querySelector('[data-single-position]')?.toggleAttribute('hidden', isCouple);
      this.querySelector('[data-couple-position]')?.toggleAttribute('hidden', !isCouple);
      this.querySelector('[data-single-feel]')?.toggleAttribute('hidden', isCouple);
      this.querySelector('[data-couple-feel]')?.toggleAttribute('hidden', !isCouple);
    }

    updatePreview(selector, source) {
      const wrapper = this.querySelector(selector);
      const image = wrapper?.querySelector('img');
      if (!wrapper || !image) return;

      if (source) {
        image.src = source;
        wrapper.hidden = false;
      } else {
        image.removeAttribute('src');
        wrapper.hidden = true;
      }
    }

    updateSizeGuidance() {
      const guidance = {
        single: {
          copy: 'Full and Queen provide extra room, while Twin XL works well for taller sleepers.',
          notes: { 'Twin XL': 'Great for height', Full: 'More room', Queen: 'Most space' },
        },
        couple: {
          copy: 'Queen or larger generally provides better shared space and comfort for couples.',
          notes: { Queen: 'Good match', King: 'Best shared space', 'California King': 'Best for height' },
        },
        child: {
          copy: 'Full and Queen sizes provide room to grow without requiring another size change soon.',
          notes: { Full: 'Room to grow', Queen: 'Long-term option' },
        },
        guest: {
          copy: 'Queen is a versatile guest-room choice, while Full can work well in smaller rooms.',
          notes: { Full: 'Space saver', Queen: 'Most versatile' },
        },
      };

      const current = guidance[this.state.sleepers];
      const rationale = this.querySelector('[data-size-rationale]');
      if (rationale) rationale.textContent = current?.copy || '';

      this.querySelectorAll('[data-field="size"]').forEach((button) => {
        const note = button.querySelector('[data-size-note]');
        if (note) note.textContent = current?.notes?.[button.dataset.value] || '';
      });
    }

    getActiveSteps() {
      return this.state.sleepers === 'couple' ? [1, 2, 3, 4, 5, 6, 7] : [1, 2, 3, 4, 5, 7];
    }

    setStep(step, moveFocus = true) {
      this.currentStep = step;
      this.querySelectorAll('[data-step]').forEach((section) => {
        section.hidden = Number(section.dataset.step) !== step;
      });

      const activeSteps = this.getActiveSteps();
      const position = Math.max(0, activeSteps.indexOf(step));
      const current = position + 1;
      const total = activeSteps.length;
      const percentage = (current / total) * 100;

      if (this.stepLabel) this.stepLabel.textContent = `Step ${current} of ${total}`;
      if (this.progress) this.progress.style.width = `${percentage}%`;
      if (this.progressbar) {
        this.progressbar.setAttribute('aria-valuemin', '1');
        this.progressbar.setAttribute('aria-valuemax', String(total));
        this.progressbar.setAttribute('aria-valuenow', String(current));
      }

      this.updateStepValidity(step);
      if (moveFocus) {
        const legend = this.querySelector(`[data-step="${step}"] legend`);
        if (legend) {
          legend.setAttribute('tabindex', '-1');
          legend.focus({ preventScroll: true });
        }
      }
    }

    updateStepValidity(step) {
      const section = this.querySelector(`[data-step="${step}"]`);
      if (!section) return;

      let valid = false;
      switch (step) {
        case 1:
          valid = Boolean(this.state.sleepers && this.state.size);
          break;
        case 2:
          valid = this.state.sleepers === 'couple'
            ? Boolean(this.state.position_1 && this.state.position_2)
            : Boolean(this.state.position);
          break;
        case 3:
          valid = Boolean(this.state.support);
          break;
        case 4:
          valid = this.state.sleepers === 'couple'
            ? Boolean(this.state.feel_1 && this.state.feel_2)
            : Boolean(this.state.feel);
          break;
        case 5:
          valid = Boolean(this.state.temperature);
          break;
        case 6:
          valid = Boolean(this.state.motion);
          break;
        case 7:
          valid = Boolean(this.state.budget);
          break;
      }

      section.querySelector('[data-next], [data-finish]')?.toggleAttribute('disabled', !valid);
    }

    scoreMatches() {
      const scores = { hybrid: 0, innerspring: 0, memoryFoam: 0, latex: 0 };
      const add = (key, amount) => { scores[key] += amount; };

      const positions = this.state.sleepers === 'couple'
        ? [this.state.position_1, this.state.position_2]
        : [this.state.position];

      positions.forEach((position) => {
        if (position === 'side') {
          add('memoryFoam', 4); add('hybrid', 3); add('latex', 1);
        } else if (position === 'back') {
          add('hybrid', 4); add('latex', 3); add('innerspring', 2);
        } else if (position === 'stomach') {
          add('innerspring', 4); add('latex', 3); add('hybrid', 2);
        }
      });

      const feels = this.state.sleepers === 'couple'
        ? [this.state.feel_1, this.state.feel_2]
        : [this.state.feel];

      feels.forEach((feel) => {
        if (feel === 'firm') {
          add('innerspring', 4); add('latex', 3); add('hybrid', 2);
        } else if (feel === 'medium') {
          add('hybrid', 4); add('latex', 3); add('memoryFoam', 2); add('innerspring', 1);
        } else if (feel === 'soft') {
          add('memoryFoam', 4); add('hybrid', 3); add('latex', 2);
        }
      });

      if (this.state.support === 'responsive') {
        add('innerspring', 5); add('hybrid', 2); add('latex', 1);
      } else if (this.state.support === 'adaptive') {
        add('hybrid', 5); add('latex', 4); add('memoryFoam', 1);
      } else if (this.state.support === 'contour') {
        add('memoryFoam', 5); add('hybrid', 3); add('latex', 1);
      }

      if (this.state.temperature === 'hot') {
        add('latex', 4); add('hybrid', 3); add('innerspring', 3);
      } else if (this.state.temperature === 'neutral') {
        add('hybrid', 2); add('latex', 2); add('innerspring', 1); add('memoryFoam', 1);
      } else if (this.state.temperature === 'cool') {
        add('memoryFoam', 3); add('hybrid', 1);
      }

      if (this.state.motion === 'low') {
        add('innerspring', 2); add('latex', 2);
      } else if (this.state.motion === 'moderate') {
        add('hybrid', 3); add('latex', 2); add('memoryFoam', 1);
      } else if (this.state.motion === 'high') {
        add('memoryFoam', 5); add('hybrid', 4); add('latex', 1);
      }

      if (this.state.sleepers === 'couple') {
        add('hybrid', 3); add('memoryFoam', 2);
      } else if (this.state.sleepers === 'child') {
        add('hybrid', 2); add('memoryFoam', 2);
      } else if (this.state.sleepers === 'guest') {
        add('hybrid', 2); add('innerspring', 2);
      } else {
        add('hybrid', 1); add('latex', 1);
      }

      if (this.state.budget === 'under-1000') {
        add('innerspring', 4); add('memoryFoam', 2); add('hybrid', 1);
      } else if (this.state.budget === '1000-1999') {
        add('hybrid', 3); add('memoryFoam', 2); add('latex', 1);
      } else if (this.state.budget === '2000-plus') {
        add('hybrid', 3); add('latex', 3); add('memoryFoam', 1);
      }

      return Object.entries(scores)
        .map(([key, score]) => ({ key, score }))
        .sort((a, b) => b.score - a.score);
    }

    getMatchConfig(key) {
      const configs = {
        hybrid: {
          title: 'Hybrid Mattresses',
          copy: 'Balanced cushioning, airflow, and responsive support for a broad range of sleepers.',
          url: this.dataset.hybridUrl,
        },
        innerspring: {
          title: 'Innerspring Mattresses',
          copy: 'Traditional lift, stronger bounce, and open airflow from a supportive coil system.',
          url: this.dataset.innerspringUrl,
        },
        memoryFoam: {
          title: 'Memory Foam Mattresses',
          copy: 'Body-conforming comfort, pressure relief, and strong motion isolation.',
          url: this.dataset.memoryFoamUrl,
        },
        latex: {
          title: 'Latex Mattresses',
          copy: 'Responsive cushioning, durable support, and naturally breathable comfort.',
          url: this.dataset.latexUrl,
        },
      };
      return configs[key];
    }

    buildCollectionUrl(baseUrl) {
      const url = new URL(baseUrl || '/collections/all', window.location.origin);
      if (this.state.size) url.searchParams.set('filter.v.option.size', this.state.size);

      if (this.state.budget === 'under-1000') {
        url.searchParams.set('filter.v.price.gte', '0');
        url.searchParams.set('filter.v.price.lte', '1000');
      } else if (this.state.budget === '1000-1999') {
        url.searchParams.set('filter.v.price.gte', '1000');
        url.searchParams.set('filter.v.price.lte', '1999');
      } else if (this.state.budget === '2000-plus') {
        url.searchParams.set('filter.v.price.gte', '2000');
      }

      return `${url.pathname}${url.search}`;
    }

    renderResults() {
      const recommendations = this.querySelector('[data-recommendations]');
      if (!recommendations) return;
      recommendations.replaceChildren();

      this.scoreMatches().slice(0, 3).forEach((match, index) => {
        const config = this.getMatchConfig(match.key);
        const card = document.createElement('article');
        card.className = 'sleep-city-custom-selector__recommendation';

        const badge = document.createElement('p');
        badge.className = 'sleep-city-custom-selector__recommendation-badge';
        badge.textContent = index === 0 ? 'Best Match' : 'Strong Match';

        const heading = document.createElement('h3');
        heading.textContent = config.title;

        const copy = document.createElement('p');
        copy.textContent = config.copy;

        const link = document.createElement('a');
        link.className = 'sc-btn sc-btn-primary';
        link.href = this.buildCollectionUrl(config.url);
        link.textContent = `Shop ${config.title}`;

        card.append(badge, heading, copy, link);
        recommendations.append(card);
      });

      this.renderSummary();
      this.quiz.hidden = true;
      this.results.hidden = false;

      const heading = this.querySelector('[data-results-heading]');
      heading?.focus({ preventScroll: true });
      this.results.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    renderSummary() {
      const summary = this.querySelector('[data-summary]');
      const list = this.querySelector('[data-summary-list]');
      if (!summary || !list) return;

      if (this.dataset.showSummary !== 'true') {
        summary.hidden = true;
        return;
      }

      summary.hidden = false;
      list.replaceChildren();

      const labels = {
        sleepers: { single: 'Single Sleeper', couple: 'Couple', child: 'Child / Teen', guest: 'Guest Room' },
        position: { side: 'Side', back: 'Back', stomach: 'Stomach' },
        support: { responsive: 'Responsive Lift', adaptive: 'Adaptive Balance', contour: 'Deep Contour' },
        feel: { firm: 'Firm', medium: 'Medium', soft: 'Soft' },
        temperature: { hot: 'Runs Hot', neutral: 'Neutral', cool: 'Runs Cool' },
        motion: { low: 'Low', moderate: 'Moderate', high: 'High' },
        budget: { 'under-1000': 'Under $1,000', '1000-1999': '$1,000–$1,999', '2000-plus': '$2,000+' },
      };

      const values = [
        ['Sleepers', labels.sleepers[this.state.sleepers]],
        ['Size', this.state.size],
        ['Position', this.state.sleepers === 'couple'
          ? `${labels.position[this.state.position_1]} / ${labels.position[this.state.position_2]}`
          : labels.position[this.state.position]],
        ['Support', labels.support[this.state.support]],
        ['Comfort', this.state.sleepers === 'couple'
          ? `${labels.feel[this.state.feel_1]} / ${labels.feel[this.state.feel_2]}`
          : labels.feel[this.state.feel]],
        ['Temperature', labels.temperature[this.state.temperature]],
        ['Budget', labels.budget[this.state.budget]],
      ];

      if (this.state.sleepers === 'couple') {
        values.splice(6, 0, ['Motion Isolation', labels.motion[this.state.motion]]);
      }

      values.forEach(([term, description]) => {
        const item = document.createElement('div');
        item.className = 'sleep-city-custom-selector__summary-item';
        const dt = document.createElement('dt');
        dt.textContent = term;
        const dd = document.createElement('dd');
        dd.textContent = description || '—';
        item.append(dt, dd);
        list.append(item);
      });
    }

    restart() {
      Object.keys(this.state).forEach((key) => { this.state[key] = ''; });
      this.querySelectorAll('[data-field]').forEach((button) => button.setAttribute('aria-pressed', 'false'));
      this.querySelector('[data-size-group]')?.setAttribute('hidden', '');
      this.updatePreview('[data-sleeper-preview]', '');
      this.updatePreview('[data-position-preview]', '');
      this.updateSleeperLayout();
      this.results.hidden = true;
      this.quiz.hidden = false;
      this.setStep(1);
    }
  }

  customElements.define('sleepcity-mattress-selector', SleepCityMattressSelector);
})();
